import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight, ShieldAlert, Radar } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ImportanceBadge from "../components/intel/ImportanceBadge";
import ShareBar from "../components/intel/ShareBar";
import { SkeletonCards, ErrorState, EmptyState } from "../components/intel/IntelStates";
import { intelApi, fmtDate } from "../lib/intelApi";
import { useLenis } from "../hooks/useLenis";

const STATS = [
  { key: "total", label: "Total updates" },
  { key: "security", label: "Security-related" },
  { key: "iam", label: "IAM-related" },
  { key: "breaking", label: "Breaking / deprecations", accent: true },
  { key: "actions", label: "Recommended actions", accent: true },
];

const weekTitle = (w) => {
  const [y, n] = w.split("-W");
  return `Week ${parseInt(n, 10)}, ${y}`;
};

export default function WeeklyBriefPage({ theme, toggleTheme }) {
  useLenis();
  const { week } = useParams();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["intel-brief", week],
    queryFn: () => intelApi.brief(week),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `This Week in Cloud Security — ${week} · Madan Aritakula`;
    return () => {
      document.title = "Madan Aritakula — Cloud Security Engineer | AWS · Azure · GCP · OCI | IAM, SIEM pipelines, CSPM";
    };
  }, [week]);

  const endDisplay = data ? new Date(new Date(data.window.to).getTime() - 86400000).toISOString() : null;
  const navBtn =
    "inline-flex items-center gap-1.5 border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-accent hover:text-accent transition-colors";

  return (
    <div className="grain relative min-h-screen bg-background text-foreground selection:bg-accent selection:text-accent-foreground">
      <Nav theme={theme} toggleTheme={toggleTheme} />
      <main data-testid="brief-page" className="pt-28 md:pt-40 pb-24 md:pb-40">
        <div className="mx-auto max-w-[1200px] px-6 md:px-10 space-y-12 md:space-y-16">
          <header className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="md:col-span-3">
              <div className="font-mono text-xs md:text-sm text-accent tracking-[0.2em] uppercase">Weekly brief</div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground" data-testid="brief-week-label">
                {weekTitle(week)}
              </div>
              {data && (
                <div className="mt-2 font-mono text-[11px] tracking-[0.1em] text-muted-foreground">
                  {fmtDate(data.window.from)} → {fmtDate(endDisplay)}
                </div>
              )}
              <div className="mt-6 flex gap-2">
                {data?.prev_week && (
                  <Link to={`/brief/${data.prev_week}`} data-testid="brief-prev-week" className={navBtn}>
                    <ChevronLeft size={12} /> {data.prev_week}
                  </Link>
                )}
                {data?.next_week && (
                  <Link to={`/brief/${data.next_week}`} data-testid="brief-next-week" className={navBtn}>
                    {data.next_week} <ChevronRight size={12} />
                  </Link>
                )}
              </div>
            </motion.div>
            <div className="md:col-span-9">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] uppercase"
                data-testid="brief-title"
              >
                This Week in
                <br />
                <span className="text-accent">Cloud Security</span>.
              </motion.h1>
              <p className="mt-5 text-base md:text-lg text-foreground/80 max-w-[56ch]">
                An AI-ranked brief of what changed across AWS, Azure, GCP, Kubernetes, Terraform, GitHub, Ansible and Azure
                DevOps — curated against my cloud-security profile by{" "}
                <Link to="/" className="text-accent hover:underline">
                  Madan Aritakula
                </Link>
                .
              </p>
              <div className="mt-6">
                <ShareBar week={week} showOpen={false} showArchive />
              </div>
            </div>
          </header>

          {isLoading && <SkeletonCards count={2} />}
          {isError && <ErrorState onRetry={refetch} message="This week could not be loaded. Check the week format (e.g. 2026-W37)." />}

          {data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border" data-testid="brief-stats">
                {STATS.map((s, i) => (
                  <div key={s.key} className={`bg-background p-5 md:p-6 ${i === 4 ? "col-span-2 md:col-span-1" : ""}`}>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{s.label}</div>
                    <div className={`mt-2 font-heading font-black text-3xl md:text-4xl tracking-tighter tabular-nums ${s.accent ? "text-accent" : ""}`}>
                      {data.stats[s.key]}
                    </div>
                  </div>
                ))}
              </div>

              <section>
                <div className="flex items-center gap-3 mb-6">
                  <ShieldAlert size={14} className="text-accent" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em]">Top 5 things to know</span>
                </div>
                {data.top.length === 0 ? (
                  <EmptyState title="Quiet week" body="No processed updates were published in this ISO week." />
                ) : (
                  <ol className="border-t border-border" data-testid="brief-top5">
                    {data.top.map((item, i) => (
                      <li key={item.id} className="group grid grid-cols-[auto_1fr] md:grid-cols-[80px_1fr_auto] gap-x-5 md:gap-x-8 gap-y-3 py-6 md:py-8 border-b border-border" data-testid={`brief-top-${i + 1}`}>
                        <div className="font-heading font-black text-3xl md:text-5xl tracking-tighter text-accent leading-none tabular-nums">{String(i + 1).padStart(2, "0")}</div>
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            <ImportanceBadge level={item.importance} />
                            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                              {item.technology} · {item.category} · {fmtDate(item.published_at)}
                            </span>
                          </div>
                          <h3 className="mt-3 font-heading font-black text-lg md:text-2xl tracking-tight leading-tight group-hover:text-accent transition-colors">
                            <a href={item.url} target="_blank" rel="noopener noreferrer">{item.title}</a>
                          </h3>
                          <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-foreground/85 max-w-[72ch]">{item.ai_summary}</p>
                          {item.why_it_matters && <p className="mt-3 text-sm leading-relaxed text-foreground/70 max-w-[72ch]">{item.why_it_matters}</p>}
                          {item.recommendation && (
                            <p className="mt-3 text-sm text-muted-foreground max-w-[72ch]">
                              <span className="font-mono text-[10px] uppercase tracking-[0.2em] mr-2">Action</span>
                              {item.recommendation}
                            </p>
                          )}
                        </div>
                        <a href={item.url} target="_blank" rel="noopener noreferrer" className="col-start-2 md:col-start-3 self-start inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] border border-border px-4 py-2.5 hover:border-accent hover:text-accent transition-colors">
                          Source <ArrowUpRight size={13} />
                        </a>
                      </li>
                    ))}
                  </ol>
                )}
              </section>

              {data.high.length > 0 && (
                <section data-testid="brief-high-list">
                  <div className="flex items-center gap-3 mb-6">
                    <Radar size={14} className="text-accent" />
                    <span className="font-mono text-[11px] uppercase tracking-[0.25em]">All Critical &amp; High updates · {data.high.length}</span>
                  </div>
                  <ul className="border-t border-border">
                    {data.high.map((item) => (
                      <li key={item.id} className="group flex flex-wrap md:flex-nowrap items-start gap-x-4 gap-y-2 py-4 border-b border-border">
                        <span className="font-mono text-[11px] tracking-[0.1em] text-muted-foreground w-20 shrink-0 pt-1">{fmtDate(item.published_at)}</span>
                        <ImportanceBadge level={item.importance} className="shrink-0" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground shrink-0 pt-1 w-28">{item.technology}</span>
                        <a href={item.url} target="_blank" rel="noopener noreferrer" className="font-heading font-bold text-sm md:text-base tracking-tight leading-snug group-hover:text-accent transition-colors flex-1 min-w-[200px]">
                          {item.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <div className="border border-border p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground max-w-[56ch]">
                  This brief is generated automatically from official vendor feeds and refreshed every 6 hours. Explore the full searchable feed and timeline on the dashboard.
                </p>
                <Link to="/intelligence" data-testid="brief-open-dashboard" className="shrink-0 inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors">
                  Open dashboard <ArrowUpRight size={14} />
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
